import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { MapView } from "@/components/MapView";
import { QuoteDialog } from "@/components/QuoteDialog";
import api from "@/lib/api";
import { ArrowLeft, MapPin, Check, Phone } from "lucide-react";

export default function MachineryDetail() {
  const { id } = useParams();
  const [m, setM] = useState(null);

  useEffect(() => { api.get(`/machinery/${id}`).then((r) => setM(r.data)).catch(() => setM(false)); }, [id]);

  if (m === null) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="h-10 w-10 border-2 border-brand border-t-transparent rounded-full animate-spin" /></div>;
  if (!m) return <div className="min-h-screen bg-background"><Navbar /><div className="max-w-3xl mx-auto px-6 py-32 text-center"><h1 className="font-display text-4xl">Equipo no encontrado</h1><Link to="/catalogo" className="text-brand mt-4 inline-block">← Volver al catálogo</Link></div></div>;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
        <Link to="/catalogo" data-testid="back-to-catalog" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-brand transition-colors mb-8">
          <ArrowLeft size={16} /> Volver al catálogo
        </Link>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Image */}
          <div>
            <div className="aspect-[4/3] border border-border overflow-hidden">
              <img src={m.images?.[0]} alt={m.name} className="h-full w-full object-cover" data-testid="detail-image" />
            </div>
          </div>

          {/* Info */}
          <div>
            <div className="flex gap-2 mb-3">
              <span className="text-xs font-semibold px-2 py-1 bg-brand text-black uppercase tracking-wider">{m.sector}</span>
              <span className="text-xs font-semibold px-2 py-1 border border-border uppercase tracking-wider">{m.listing_type === "ambos" ? "Alquiler y venta" : m.listing_type}</span>
            </div>
            <div className="overline text-brand mb-2">{m.brand} · {m.model} {m.year ? `· ${m.year}` : ""}</div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter leading-none mb-4" data-testid="detail-name">{m.name}</h1>
            <p className="text-muted-foreground mb-6">{m.description}</p>

            <div className="flex items-center gap-1 text-sm text-muted-foreground mb-6">
              <MapPin size={14} className="text-brand" /> {m.location?.address}, {m.location?.city}
            </div>

            {/* Prices */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              {m.price_rent_day != null && (
                <div className="border border-border p-5">
                  <div className="overline text-muted-foreground mb-1">Alquiler</div>
                  <div className="font-display font-black text-3xl text-brand tracking-tight">{m.currency} {m.price_rent_day}</div>
                  <div className="text-xs text-muted-foreground">por día</div>
                </div>
              )}
              {m.price_sale != null && (
                <div className="border border-border p-5">
                  <div className="overline text-muted-foreground mb-1">Venta</div>
                  <div className="font-display font-black text-3xl tracking-tight">{m.currency} {m.price_sale.toLocaleString()}</div>
                  <div className="text-xs text-muted-foreground">precio total</div>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-4">
              <QuoteDialog machinery={m} trigger={
                <button data-testid="request-quote-btn" className="flex-1 min-w-[200px] h-12 bg-brand text-black font-semibold hover:bg-brand-hover transition-colors">
                  Solicitar cotización
                </button>
              } />
              <a href="tel:+51900000000" className="h-12 px-6 flex items-center gap-2 border border-border hover:border-brand transition-colors font-semibold">
                <Phone size={16} /> Llamar
              </a>
            </div>
          </div>
        </div>

        {/* Specs */}
        {m.specs && Object.keys(m.specs).length > 0 && (
          <div className="mt-16">
            <div className="overline text-brand mb-2">Especificaciones técnicas</div>
            <h2 className="font-display font-black text-3xl tracking-tight mb-6">FICHA TÉCNICA</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-border">
              {Object.entries(m.specs).map(([k, v]) => (
                <div key={k} className="border-r border-b border-border p-5">
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{k}</div>
                  <div className="font-display font-bold text-xl flex items-center gap-2"><Check size={16} className="text-brand" /> {v}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Location */}
        <div className="mt-16">
          <div className="overline text-brand mb-2">Ubicación</div>
          <h2 className="font-display font-black text-3xl tracking-tight mb-6">DÓNDE ESTÁ EL EQUIPO</h2>
          <MapView items={[m]} height="400px" center={[m.location.lat, m.location.lng]} zoom={9} />
        </div>
      </div>
      <Footer />
    </div>
  );
}
